import { useState, useRef, useCallback } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Loader2, ZoomIn, ZoomOut, RotateCcw, Check } from 'lucide-react';

const ImageCropDialog = ({ 
  open, 
  onClose, 
  imageUrl, 
  onSave, 
  aspectRatio = 1 
}) => {
  const [crop, setCrop] = useState({
    unit: '%',
    width: 80,
    height: 80,
    x: 10,
    y: 10
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [zoom, setZoom] = useState(100);
  const [saving, setSaving] = useState(false);
  const imgRef = useRef(null);

  const onImageLoad = useCallback((e) => {
    imgRef.current = e.currentTarget;
    
    // Set initial crop centered
    const { width, height } = e.currentTarget;
    const cropSize = Math.min(width, height) * 0.8;
    const newCrop = {
      unit: 'px',
      width: cropSize,
      height: cropSize,
      x: (width - cropSize) / 2,
      y: (height - cropSize) / 2
    };
    setCrop(newCrop);
  }, []);

  const handleZoomChange = (value) => {
    setZoom(value[0]);
  };

  const resetCrop = () => {
    if (imgRef.current) {
      const { width, height } = imgRef.current;
      const cropSize = Math.min(width, height) * 0.8;
      setCrop({
        unit: 'px',
        width: cropSize,
        height: cropSize,
        x: (width - cropSize) / 2,
        y: (height - cropSize) / 2
      });
    }
    setZoom(100);
  };

  const getCroppedImg = async () => {
    if (!imgRef.current || !completedCrop) return null;

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = completedCrop.width * scaleX;
    canvas.height = completedCrop.height * scaleY;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/jpeg', 0.9);
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const croppedBlob = await getCroppedImg();
      if (croppedBlob) {
        await onSave(croppedBlob);
      }
      onClose();
    } catch (error) {
      console.error('Errore ritaglio immagine:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
            Modifica Immagine
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Area di crop */}
          <div className="relative bg-slate-100 rounded-lg overflow-hidden flex items-center justify-center min-h-[300px]">
            {imageUrl && (
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={aspectRatio}
                className="max-h-[400px]"
              >
                <img
                  ref={imgRef}
                  src={imageUrl}
                  alt="Immagine da ritagliare"
                  onLoad={onImageLoad}
                  style={{ 
                    maxHeight: '400px',
                    transform: `scale(${zoom / 100})`,
                    transformOrigin: 'center center'
                  }}
                  crossOrigin="anonymous"
                />
              </ReactCrop>
            )}
          </div>

          {/* Controlli zoom */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Zoom</Label>
              <span className="text-sm text-slate-500">{zoom}%</span>
            </div>
            <div className="flex items-center gap-3">
              <ZoomOut className="h-4 w-4 text-slate-400" />
              <Slider
                value={[zoom]}
                onValueChange={handleZoomChange}
                min={50}
                max={200}
                step={5}
                className="flex-1"
              />
              <ZoomIn className="h-4 w-4 text-slate-400" />
            </div>
          </div>

          {/* Istruzioni */}
          <p className="text-sm text-slate-500 text-center">
            Trascina gli angoli del riquadro per selezionare l'area da ritagliare
          </p>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2 mt-4">
          <Button variant="outline" onClick={resetCrop} className="w-full sm:w-auto">
            <RotateCcw className="h-4 w-4 mr-2" />
            Ripristina
          </Button>
          <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">
            Annulla
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving || !completedCrop}
            className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            Salva Ritaglio
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImageCropDialog;
